<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { Badge } from '~/components/ui/badge'
import { Separator } from '~/components/ui/separator'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'

interface IdleWarningData {
  countdown: number
  onStayLoggedIn: () => void
  onLogoutNow: () => void
}

const TOTAL_COUNTDOWN_TIME = 120 // 2 minutes in seconds

const { t } = useI18n()

const isVisible = ref(false)
const countdown = ref(TOTAL_COUNTDOWN_TIME)
const countdownInterval = ref<ReturnType<typeof setInterval> | null>(null)
const onStayLoggedIn = ref<(() => void) | null>(null)
const onLogoutNow = ref<(() => void) | null>(null)

const formattedTime = computed((): string => {
  const minutes = Math.floor(countdown.value / 60)
  const remainingSeconds = countdown.value % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
})

const isUrgent = computed((): boolean => countdown.value <= 30)
const progressPercentage = computed((): number => {
  return ((TOTAL_COUNTDOWN_TIME - countdown.value) / TOTAL_COUNTDOWN_TIME) * 100
})

const showWarning = (data: IdleWarningData): void => {
  isVisible.value = true
  countdown.value = data.countdown
  onStayLoggedIn.value = data.onStayLoggedIn
  onLogoutNow.value = data.onLogoutNow

  startCountdown()

  nextTick(() => {
    const modal = document.querySelector('[role="dialog"]') as HTMLElement
    modal?.focus()
  })
}

const hideWarning = (): void => {
  isVisible.value = false
  stopCountdown()
}

const startCountdown = (): void => {
  stopCountdown()

  countdownInterval.value = setInterval(() => {
    countdown.value -= 1

    if (countdown.value <= 0) {
      handleLogoutNow()
    }
  }, 1000)
}

const stopCountdown = (): void => {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value)
    countdownInterval.value = null
  }
}

const handleStayLoggedIn = (): void => {
  hideWarning()
  onStayLoggedIn.value?.()
}

const handleLogoutNow = (): void => {
  hideWarning()
  onLogoutNow.value?.()
}

const handleKeydown = (event: KeyboardEvent): void => {
  if (isVisible.value && event.key === 'Escape') {
    // Prevent closing modal with Escape key during session timeout
    event.preventDefault()
    event.stopPropagation()
  }
}

const handleShowIdleWarning = (event: Event): void => {
  const customEvent = event as CustomEvent<IdleWarningData>
  if (customEvent.detail) {
    showWarning(customEvent.detail)
  }
}

const handleHideIdleWarning = (): void => {
  hideWarning()
}

onMounted(() => {
  window.addEventListener('show-idle-warning', handleShowIdleWarning)
  window.addEventListener('hide-idle-warning', handleHideIdleWarning)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('show-idle-warning', handleShowIdleWarning)
  window.removeEventListener('hide-idle-warning', handleHideIdleWarning)
  document.removeEventListener('keydown', handleKeydown)
  stopCountdown()
})
</script>

<template>
  <Dialog v-model:open="isVisible" :modal="true">
    <DialogContent
      class="sm:max-w-[550px] gap-0 p-0"
      @escape-key-down.prevent
      @pointer-down-outside.prevent
      @interact-outside.prevent
    >
      <div
        class="flex flex-col space-y-1.5 text-center sm:text-left p-6 pb-4"
        :class="isUrgent ? 'bg-destructive/5' : 'bg-amber-30 dark:bg-amber-950/20'"
      >
        <DialogHeader>
          <DialogTitle
            class="flex items-center gap-2 text-lg font-semibold leading-none tracking-tight"
          >
            <div
              class="flex h-8 w-8 items-center justify-center rounded-full"
              :class="
                isUrgent ? 'bg-destructive/20 text-destructive' : 'bg-amber-500/20 text-amber-600'
              "
            >
              <Icon icon="lucide:alert-triangle" class="h-4 w-4" />
            </div>
            {{ t('Session Timeout Warning') }}
          </DialogTitle>
          <DialogDescription class="text-sm text-muted-foreground mt-2">
            {{ t("You've been inactive for a while. Your session will expire soon.") }}
          </DialogDescription>
        </DialogHeader>
      </div>

      <Separator />

      <div class="p-6 pt-4">
        <div class="flex flex-col items-center space-y-4">
          <div class="relative">
            <svg class="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                stroke-width="8"
                fill="none"
                class="text-muted-foreground/20"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                stroke-width="8"
                fill="none"
                :class="isUrgent ? 'text-destructive' : 'text-amber-500'"
                :stroke-dasharray="`${2 * Math.PI * 45}`"
                :stroke-dashoffset="`${2 * Math.PI * 45 * (1 - progressPercentage / 100)}`"
                class="transition-all duration-300 ease-in-out"
                stroke-linecap="round"
              />
            </svg>
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="text-center">
                <div
                  class="text-2xl font-bold tabular-nums transition-colors duration-300"
                  :class="isUrgent ? 'text-destructive' : 'text-amber-600'"
                >
                  {{ formattedTime }}
                </div>
                <div class="text-xs text-muted-foreground">
                  {{ t('remaining') }}
                </div>
              </div>
            </div>
          </div>

          <Badge :variant="isUrgent ? 'destructive' : 'secondary'" class="font-normal">
            <Icon :icon="isUrgent ? 'lucide:clock' : 'lucide:timer'" class="mr-1 h-3 w-3" />
            {{ isUrgent ? t('Session expiring soon') : t('Session timeout warning') }}
          </Badge>

          <!-- Information -->
          <div class="text-center space-y-2">
            <p class="text-sm font-medium">
              {{ t('Do you want to stay logged in?') }}
            </p>
            <p class="text-xs text-muted-foreground">
              {{ t('Choose an option below to continue or logout now.') }}
            </p>
          </div>
        </div>
      </div>

      <Separator />

      <DialogFooter class="p-6 pt-4">
        <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 w-full">
          <Button variant="outline" size="sm" class="sm:w-auto w-full" @click="handleLogoutNow">
            <Icon icon="lucide:log-out" class="mr-2 h-4 w-4" />
            {{ t('Logout Now') }}
          </Button>
          <Button
            size="sm"
            class="sm:w-auto w-full"
            :class="isUrgent ? 'bg-destructive hover:bg-destructive/90' : ''"
            @click="handleStayLoggedIn"
          >
            <Icon icon="lucide:refresh-cw" class="mr-2 h-4 w-4" />
            {{ t('Stay Logged In') }}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.05);
    opacity: 0.7;
  }
}

.dialog-content[data-urgent='true'] {
  animation: pulse-ring 2s ease-in-out infinite alternate;
}

:deep(.dialog-overlay) {
  z-index: 50;
}

:deep(.dialog-content) {
  z-index: 51;
}
</style>
